<template>
  <div class="i-login">
    <iBackground
      :background-image="bgSquiggleGreen"
      :color="white"
    />

    <div class="header">
      <div class="logo">
        <a @click="goToHome()"
          ><img
            :src="logo"
            alt="JobTest.org"
            class="logo-img"
          />
        </a>
      </div>
      <iBaseButton
        v-bind="BUTTON_STYLE.header"
        size="small"
        text="Find Your Calling"
        @clicked="goToTest()"
      />
    </div>

    <div class="wrap">
      <div
        v-if="!!MAGIC_AUTH_FAILED || !!OAUTH_FAILED"
        class="auth-failed"
      >
        <div v-if="!!MAGIC_AUTH_FAILED"><strong>Your magic link has expired!</strong> Please log in again.</div>
        <div v-if="!!OAUTH_FAILED">
          Something went wrong! <strong>Please try again.</strong>
          <br />
          <small>
            <i>If the problem persists, contact our <a href="mailto:support@jobtest.org">support team</a>.</i>
          </small>
        </div>
      </div>
      <div class="card">
        <template v-if="!(uiData.submissionSuccess || uiData.submissionError)">
          <h1 class="title">Sign in</h1>

          <form
            novalidate
            @submit.prevent="submitForm()"
          >
            <label class="label">Email Address:</label>

            <div
              v-if="uiData.emailError"
              class="error-message"
            >
              {{ uiData.emailError }}
            </div>

            <input
              id="email"
              v-model="userData.email"
              type="email"
              name="email"
              autocomplete="email"
              autocapitalize="off"
              spellcheck="false"
              class="input-field"
              maxlength="256"
              autofocus
            />

            <div>
              <iBaseButton
                class="cta"
                v-bind="BUTTON_STYLE.email"
                :text="uiData.submitting ? 'Please wait...' : 'Continue with Email'"
                :is-disabled="!userData.email || uiData.submitting"
                @clicked="submitForm()"
              />
            </div>
          </form>
        </template>

        <template v-if="uiData.submissionSuccess">
          <h1 class="title">Magic Link Sent</h1>

          <div class="info-body">
            If we find an account associated with your email, you will receive a magic link shortly. <br /><br />
            This link is valid for <strong>24 hours</strong> for your security. <br /><br />
            Didn’t receive the email? Check your spam folder or reach out to our
            <a href="mailto:support@jobtest.org">support team</a>.
          </div>

          <div>
            <iBaseButton
              class="cta"
              v-bind="BUTTON_STYLE.email"
              text="Back to Jobtest.org"
              @clicked="goToHome()"
            />
          </div>
        </template>

        <template v-if="uiData.submissionError">
          <h1 class="title c-error">We Couldn't Process Your Request</h1>

          <div class="info-body">
            <strong>Your purchase appears to have been refunded.</strong> <br /><br />
            If this was done in error or if you wish to make a new purchase, please contact our
            <a href="mailto:support@jobtest.org">support team</a>.
          </div>

          <div>
            <iBaseButton
              class="cta"
              v-bind="BUTTON_STYLE.email"
              text="Start the test!"
              :icon-url="iconUpArrowBlack"
              @clicked="goToTest()"
            />
          </div>
        </template>

        <template v-if="!uiData.submissionError && !uiData.submissionSuccess">
          <div class="separator">
            <iBaseDelimiter class="delimiter" /><span>OR</span><iBaseDelimiter class="delimiter" />
          </div>
          <iBaseButton
            class="cta"
            v-bind="BUTTON_STYLE.oAuth"
            text="Continue with Google"
            :pre-icon="iconGoogle"
            @clicked="goToGoogleOauth()"
          />
        </template>
      </div>

      <div class="back-cta">
        <a
          class="back-link"
          @click="uiData.backToHome ? goToHome() : showSignIn()"
        >
          <img :src="iconChevron" /> Back to {{ uiData.backToHome ? "Jobtest.org" : "Sign In" }}
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { GlobalFunctions } from "@/helpers/globalFunctions"
import { GlobalVariables } from "@/helpers/globalVariables"
import { reactive } from "vue"

import api from "@/services/apiClient"

import { black, brandOpacity100, sorbus400, white } from "@/styles/colors"

import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseDelimiter from "@/components/base/BaseDelimiter.vue"
import iBackground from "@/components/global/Background.vue"

import iconChevron from "@/assets/icons/icon-chevron-left-blue.svg"
import iconGoogle from "@/assets/icons/icon-google-color.svg"
import iconUpArrowBlack from "@/assets/icons/icon-up-arrow-black.svg"
import bgSquiggleGreen from "@/assets/jobTest/bg-squiggle-green.svg"
import logo from "@/assets/logo/logo-dark.svg"

// DATA (reactive object with properties)

const uiData = reactive({
  emailError: "",
  submitting: false,
  submissionSuccess: false,
  submissionError: false,
  backToHome: true
})

const userData = reactive({
  email: ""
})

// CONSTANT
const SHARED_BUTTON_STYLES = {
  fontWeight: "500",
  textColor: black,
  width: "100%",
  borderRadius: "8px"
}

const BUTTON_STYLE = {
  header: {
    ...SHARED_BUTTON_STYLES,
    backgroundColor: sorbus400,
    width: "fit-content",
    borderRadius: "12px"
  },
  email: {
    ...SHARED_BUTTON_STYLES,
    backgroundColor: brandOpacity100
  },
  oAuth: {
    ...SHARED_BUTTON_STYLES,
    backgroundColor: white
  }
}

const MAGIC_AUTH_FAILED = new URL(window.location.href).searchParams.get("magicAuthFailed")
const OAUTH_FAILED = new URL(window.location.href).searchParams.get("oauthFailed")

// FUNCTIONS
function showSignIn() {
  userData.email = ""
  uiData.submissionError = false
  uiData.submissionSuccess = false
  uiData.backToHome = true
}

function goToGoogleOauth() {
  // If the redirect path is set (user was redirected to the login page from another path), pass it to the Google OAuth
  const redirectPath = localStorage.getItem("redirect")
  localStorage.removeItem("redirect")
  api.auth.goToGoogleOauth(redirectPath || "/")
}

function goToHome() {
  const pageUrl = GlobalVariables.urls.root.path
  const domain = GlobalFunctions.getWebsiteDomain()
  window.location.href = `https://${domain}${pageUrl}`
}

function goToTest() {
  const pageUrl = GlobalVariables.urls.jobTest.path
  const domain = GlobalFunctions.getWebsiteDomain()
  window.location.href = `https://${domain}${pageUrl}`
}

function isValidEmail(email: string): boolean {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i
  return emailPattern.test(email)
}

function submitForm() {
  const email = userData.email.trim()

  // Validate the email
  if (!isValidEmail(email)) {
    uiData.emailError = "Please enter a valid email address."
    return
  }

  // check if the
  uiData.submitting = true
  uiData.emailError = "" // clear the error message
  api.auth.postMagicLink({ email }).then(successResponse).catch(errorResponse)
}

function successResponse() {
  uiData.submissionSuccess = true
  uiData.submissionError = false
  uiData.submitting = false
  uiData.backToHome = false
}

function errorResponse(error: any) {
  console.log(error)
  uiData.submissionError = true
  uiData.submissionSuccess = false
  uiData.submitting = false
  uiData.backToHome = false
}
</script>

<style scoped lang="scss">
$card-padding: 20px;
$card-padding-sm: 40px;
$card-padding-md: 40px;
$input-field-background: white;
$header-height: 60px;

.i-login {
  @include flex($justify: center, $align: center);

  min-height: 100vh;

  .header {
    @include flex(row, $justify: space-between, $align: center);

    width: 100%;
    height: $header-height;
    padding: 15px;
    position: fixed;
    top: 0;

    @media screen and (min-width: $breakpoint-sm) {
      padding: 15px 40px;
    }

    .logo {
      a:hover {
        cursor: pointer;
      }

      .logo-img {
        width: 160px;
      }
    }
  }

  .wrap {
    margin-top: $header-height;
    padding: 25px 0;
    width: 90%;
    max-width: 500px;

    .auth-failed {
      background-color: $bittersweet300;
      color: $black;

      @include border-radius-md;

      text-align: center;
      padding: 10px 20px;
      margin-bottom: 12px;
    }

    .card {
      background-color: rgb(244.37 244.37 244.37 / 60%);
      text-align: start;

      @include border-radius-lg;
      @include card($shadow: true, $padding: true);

      .title {
        @include text-xl;

        font-weight: bold;
        margin-bottom: 24px;

        &.c-error {
          color: $bittersweet500;
        }
      }

      .label {
        @include text-sm;

        font-weight: 600;
      }

      .input-field {
        min-height: 48px !important;
        margin-top: 10px;

        @include input-field;
      }

      .info-body {
        margin: 24px 0 28px;
      }

      .cta {
        margin: 15px auto 20px;
      }

      .back-link {
        @include text-link;

        font-weight: bold;
      }

      .error-message {
        color: $bittersweet500;
        margin-top: 8px;
        font-size: 14px;
      }
    }

    .separator {
      @include flex(row, $justify: space-around, $align: center);

      .delimiter {
        width: 40%;
      }
    }

    .back-cta {
      color: $azure-radiance950;
      text-align: center;
      margin-top: 24px;
      cursor: pointer;
    }
  }
}
</style>
